import type { ReactElement } from "react";
import { Heading, Link, Text } from "@stubhub/broadway-web";
import { useFormattedContent } from "src/shared/components/content";
import { Image } from "src/website-apps/shared/components/image";
import { DEFAULT_FALLBACK_BACKGROUND_COLOR, favoriteTeamEventCardStyles } from "./FavoriteTeamEventCard.styles";

interface FavoriteTeamEventCardProps {
  /** The team's logo, rendered centered over the colored plate. Falls back to a 3-letter abbreviation badge when absent. */
  logoUrl?: string | undefined;
  /** Full-bleed catalog photo; takes precedence over logoUrl when present. */
  teamImageUrl?: string | undefined;
  imageAlt: string;
  /** Pre-formatted shortened matchup label, e.g. "vs. Pirates" or "\@ Dodgers". */
  matchupLabel: string;
  /** Pre-formatted event date/time subtext, e.g. "Thu, 7/15 • 7:05pm". */
  dateTimeText: string;
  url: string;
  /** 3-letter team abbreviation shown in place of the logo when logoUrl is absent, e.g. "PIT". */
  fallbackAbbreviation?: string | undefined;
  /** Background color for the plate behind the logo (or the fallback abbreviation). */
  backgroundColor?: string | undefined;
  /** Pre-formatted lowest ("from") price, e.g. "$45". Omitted entirely when absent. */
  formattedFromPrice?: string | undefined;
}

export const FavoriteTeamEventCard = ({
  logoUrl,
  teamImageUrl,
  imageAlt,
  matchupLabel,
  dateTimeText,
  url,
  fallbackAbbreviation,
  backgroundColor,
  formattedFromPrice,
}: FavoriteTeamEventCardProps): ReactElement => {
  const fromPriceLabel = useFormattedContent("cid$#4099723#", [formattedFromPrice ?? ""]); // "From {0}"

  return (
    <div className={favoriteTeamEventCardStyles.root}>
      <div
        className={favoriteTeamEventCardStyles.imageContainer}
        data-testid="favorite-team-event-card-image-container-id"
        style={{ backgroundColor: backgroundColor ?? DEFAULT_FALLBACK_BACKGROUND_COLOR }}
      >
        {teamImageUrl ? (
          <Image alt={imageAlt} className={favoriteTeamEventCardStyles.teamImage} src={teamImageUrl} />
        ) : logoUrl ? (
          <Image alt={imageAlt} className={favoriteTeamEventCardStyles.logoImage} src={logoUrl} />
        ) : (
          <span className={favoriteTeamEventCardStyles.fallbackAbbreviation}>{fallbackAbbreviation}</span>
        )}
      </div>
      <div className={favoriteTeamEventCardStyles.matchupText}>
        <Heading className={favoriteTeamEventCardStyles.singleLine} variant="title" size="small" as="h3">
          {matchupLabel}
        </Heading>
        <Text className={favoriteTeamEventCardStyles.singleLine} variant="label" size="small" color="text.hover">
          {dateTimeText}
        </Text>
      </div>
      {formattedFromPrice && fromPriceLabel && (
        <div className={favoriteTeamEventCardStyles.buyButton}>
          <Text className={favoriteTeamEventCardStyles.singleLine} variant="label" size="small">
            {fromPriceLabel}
          </Text>
        </div>
      )}
      <Link className={favoriteTeamEventCardStyles.cardLink} href={url} aria-label={matchupLabel} />
    </div>
  );
};
